import React, { useMemo, useState } from "react";
import { PageHeader, Card, StatusPill } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { useAirtable, updateAirtable } from "../hooks/useAirtable";
import { str, fmtDate, indexById, linkedName } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { relabel } from "../lib/displayNames";

const DocumentQueuePage = () => {
  const docs = useAirtable("documents");
  const clients = useAirtable("clients");
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("Pending");
  const clientMap = useMemo(() => indexById(clients.records), [clients.records]);

  if (docs.loading || clients.loading) return <Loading label="Loading documents" />;
  if (docs.error) return <ErrorState message={docs.error} onRetry={docs.refetch} />;

  const allRows = docs.records.map((r) => {
    const f = r.fields || {};
    return {
      id: r.id,
      client: linkedName(f["Client Link"], clientMap),
      docName: str(f["Document Name"] || f["Document Category"]),
      service: str(f["Service Related To"]),
      uploaded: f["Upload Date"],
      uploadedBy: str(f["Uploaded By"]),
      status: f["Review Status"] || "Pending",
      hasFile: Array.isArray(f["File Attachment"]) && f["File Attachment"].length > 0,
    };
  });

  const counts = {
    All: allRows.length,
    Pending: allRows.filter((d) => d.status !== "Approved").length,
    Approved: allRows.filter((d) => d.status === "Approved").length,
  };

  const rows = allRows.filter((d) => {
    if (filter === "All") return true;
    if (filter === "Approved") return d.status === "Approved";
    return d.status !== "Approved";
  });

  const review = async (id, status) => {
    setBusyId(id);
    try {
      await updateAirtable("documents", id, { "Review Status": status });
      await docs.refetch();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="document-queue-page">
      <PageHeader
        eyebrow="DASHBOARD / DOCUMENT QUEUE"
        title="Document Queue"
        subtitle={`${counts.Pending} awaiting review • ${counts.Approved} approved.`}
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <ExportButtons
              title="Document Queue"
              filename="documents"
              testidPrefix="export-documents"
              columns={[
                { key: "client", label: "Client" },
                { key: "docName", label: "Document" },
                { key: "service", label: "Service" },
                { key: "uploaded", label: "Uploaded" },
                { key: "uploadedBy", label: "Uploaded By" },
                { key: "status", label: "Status" },
              ]}
              rows={rows}
            />
            {["Pending", "Approved", "All"].map((f) => (
              <button
                key={f}
                data-testid={`doc-filter-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 border font-heading tracking-widest text-[0.75rem] uppercase ${filter === f ? "border-[#2A1C15] bg-[#2A1C15] text-[#DCCFB9]" : "border-[rgba(42,28,21,0.18)] text-[#2A1C15] hover:border-[#2A1C15]"}`}
              >
                {f} <span className="bj-tnum ml-1">({counts[f]})</span>
              </button>
            ))}
          </div>
        }
      />

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[1000px]">
          <thead>
            <tr className="text-left">
              {["Client","Document","Service","Uploaded","By","File","Status","Actions"].map((h) => (
                <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-[#5E4D40] text-sm">No documents on file.</td></tr>}
            {rows.map((d, i) => (
              <tr key={d.id} data-testid={`doc-page-row-${i}`} className="bj-table-row">
                <td className="py-3 pr-3 text-[#2A1C15] text-sm">{d.client}</td>
                <td className="py-3 pr-3 text-[#6E5022] text-sm">{d.docName}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm">{relabel(d.service)}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm bj-tnum">{fmtDate(d.uploaded)}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm">{d.uploadedBy}</td>
                <td className="py-3 pr-3 text-sm">{d.hasFile ? <span className="text-[#14281E] text-xs font-heading tracking-widest uppercase">✓ Attached</span> : <span className="text-[#5E4D40] text-xs">—</span>}</td>
                <td className="py-3 pr-3"><StatusPill status={d.status} /></td>
                <td className="py-3 pr-3">
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`doc-page-approve-${d.id}`}
                      onClick={() => review(d.id, "Approved")}
                      disabled={busyId === d.id || d.status === "Approved"}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#14281E]/40 text-[#14281E] hover:bg-[#14281E]/10 font-heading tracking-widest text-[0.75rem] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <CheckCircle size={14} weight="bold" /> Approve
                    </button>
                    <button
                      data-testid={`doc-page-resubmit-${d.id}`}
                      onClick={() => review(d.id, "Resubmit")}
                      disabled={busyId === d.id || d.status === "Resubmit"}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#5C1A24]/40 text-[#5C1A24] hover:bg-[#5C1A24]/10 font-heading tracking-widest text-[0.75rem] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <XCircle size={14} weight="bold" /> Resubmit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default DocumentQueuePage;
